import { randomId } from '@/library/message/lib';
import { filterObject, noop } from '@/common/utilities';
import { cleanChatSystemTags } from '@/common/chatSystemTags';
import { $pr } from '@/pleased-sdk/PleasedReporting';

import { PleasedApi } from '../PleasedApi';
import { EventEmitter } from '../common/EventEmitter';
import type { VisitorDetail } from '../common/PleasedSdk.types';
import { TeardownManager } from '../common/Teardown';
import { getChatQueuePositionStream } from '../PleasedApi/sse.pleased';

import type * as Types from './PleasedChat.types';
import { TYPING_ACTIVITY_THROTTLE_MS } from './PleasedChat.types';
import { createAttachment, createChatMessage } from './PleasedChat.factory';
import { FirebaseChatService } from './FirebaseChatService';
import { serverTimestamp as fbTimestamp } from './FirebaseChatService';
import { SystemTriggerMessageBroker } from '@/common/systemMessages';
import type { SystemMessageConfig } from '@/common/api/models.pleased';
import { randomUUID } from '@/common/bot/lib';
import { analytics, COMPONENT_EVENTS } from '@/common/logs';
import { dialog } from '@/plugins/dialog';
import { devLog } from '@/common/debug';
import { captureEventException, ErrorTrackingEvent } from '@/common/error-tracking';
import { logFirebaseConnection } from '@/common/api/logger';
import {
  FIREBASE_CONNECTION_STATUS_BY_OUTCOME,
  FirebaseConnectionContext,
  FirebaseConnectionStatus,
} from './FirebaseConnection.enum';
import {
  FirebaseConnectionMonitor,
  getFirebaseConnectionTimeoutMs,
  setFirebaseConnectionStatus,
} from './FirebaseConnectionMonitor';
import { throttle } from '@/vendor/pleased-sdk/util';

// TODO: make lazy load work
// const loadService = () => import('./FirebaseChatService') as Promise<typeof import('./FirebaseChatService')>;
// const fbTimestamp = () => loadService().then((m) => m.serverTimestamp());

const SYSTEM_TRIGGER_ADMIN = 1;

export class PleasedChat extends EventEmitter<Types.PleasedChatEvents> {
  protected chatState = {
    closed: null as null | Types.FirebaseClosedChat,
    messages: {} as Record<string | number, Types.ChatMessage>,
    isReady: false,
    didAgentJoin: false,
  };

  protected chatId: number | null = null;
  protected firebaseChat: FirebaseChatService | null = null;
  protected visitor: VisitorDetail | null = null;
  protected systemMessageBroker: SystemTriggerMessageBroker | null = null;
  protected resumableChatSessionId: string = '';

  private readonly emitThrottledTypingActivity = throttle(() => {
    this.event.emit('chat:customer:activity');
  }, TYPING_ACTIVITY_THROTTLE_MS);

  /**
   * Clean up.
   */
  protected $tdm = new TeardownManager();

  constructor(protected options: Types.PleasedChatRequestMetadata) {
    super();
  }

  get isActive() {
    return !this.chatState.closed && this.chatId;
  }

  get id() {
    return this.chatId || 'new';
  }

  get isConnected() {
    return Boolean(this.isActive && this.firebaseChat?.isConnected);
  }

  get didAgentJoin() {
    return this.chatState.didAgentJoin;
  }

  protected showExistingChatRedirectDialog(): Promise<void> {
    return new Promise((resolve) => {
      let triggerDialog: { close: () => void } | null = null;

      const handleClose = () => {
        triggerDialog?.close();
        resolve();
      };

      triggerDialog = dialog({
        content: 'We noticed you already have a chat open. Once you click okay you will be redirected to that chat.',
        buttons: [
          {
            name: 'Okay',
            class: ['primary'],
            callback: handleClose,
          },
        ],
      });
    });
  }

  async create() {
    const { user, ...request }: Types.PleasedChatRequestMetadata = this.options;

    $pr.chatInitiated();

    const chatTags = request.chatTags ?? request.tags;
    const autoAddTags = request.autoAddTags ?? [];
    const systemTags = chatTags?.length ? [...autoAddTags, ...cleanChatSystemTags(chatTags)] : undefined;

    const userLogs = $pr.getLogs();
    const transactions = request.transactions ?? [];

    const chatRequest = PleasedApi.createChatRequest(
      filterObject({
        categoryId: 0,
        customerAppVersion: request.customerAppVersion,
        email: user.email,
        foreignId: user.foreignId,
        name: user.displayName,
        phone: user.phone,
        subject: request.subject,
        ...(systemTags?.length ? { systemTags } : {}),
        ticketId: request.ticketId,
        ...(userLogs.length ? { userLogs } : {}),
        transactions,
        isGuest: user.isGuest,
        chatbotSessionId: request.chatbotSessionId,
        aiSessionId: request.aiSessionId,
        aiVoiceChatSessionId: request.aiVoiceChatSessionId,
        // userSelections: request.userSelections,
      }),
      // Controller reports via captureEventException(OpenChat) — avoid duplicate API events.
      { skipErrorTracking: true }
    );
    const [
      chatResponse,
      // FirebaseChatService,
    ] = await Promise.all([
      chatRequest,
      // loadService().then((m) => m.FirebaseChatService),
    ]);

    if (chatResponse?.existingChat) {
      await this.showExistingChatRedirectDialog().catch(() => undefined);
    }

    const { chatId, customerId, firebaseToken } = chatResponse;

    const visitor = {
      displayName: user.displayName,
      id: customerId,
      email: user.email,
      foreignId: user.foreignId,
      isGuest: user.isGuest,
      phone: user.phone,
    };

    this.visitor = visitor;
    this.chatId = chatId;

    const stream = getChatQueuePositionStream({ chatId });
    stream.subscribe((res) => this.event.emit('chat:queue:position', { position: res.data.position }));
    this.$tdm.add('stop-chat-position-queue', () => {
      this.event.emit('chat:queue:position', { position: 'clear' });
      stream.close();
    });

    this.brokerCreation(chatResponse.timeSinceTriggers || []);

    await this.setupFirebaseChat({
      chatId,
      firebaseToken,
      visitor,
      onParticipationExtra: (data) => {
        if (
          data.type === 'member:join' &&
          data.member.userId !== SYSTEM_TRIGGER_ADMIN &&
          data.member.userId !== Number(visitor.id)
        ) {
          // If someone other than a trigger admin joins, we can clear the position
          this.$tdm.tearOne('stop-chat-position-queue');
        }
      },
    });

    return this;
  }

  /**
   * Sets up Firebase chat controller with event handlers
   * @param params - Configuration object
   * @param params.chatId - The chat ID
   * @param params.firebaseToken - Firebase token for authentication
   * @param params.visitor - Visitor details
   * @param params.onParticipationExtra - Optional additional participation handler for specific use cases
   */
  protected async setupFirebaseChat(params: {
    chatId: number;
    firebaseToken: string;
    visitor: VisitorDetail;
    onParticipationExtra?: (data: any) => void;
  }) {
    const { chatId, firebaseToken, visitor, onParticipationExtra } = params;
    const customerId = Number(visitor.id);
    setFirebaseConnectionStatus(FirebaseConnectionStatus.Pending);
    const firebaseConnectionMonitor = new FirebaseConnectionMonitor({
      connectionContext: FirebaseConnectionContext.ChatbotChat,
      chatId,
      timeoutMs: getFirebaseConnectionTimeoutMs(),
    });
    const unsubscribeFromFirebaseConnectionStatus = firebaseConnectionMonitor.onStatusChange((event) => {
      setFirebaseConnectionStatus(FIREBASE_CONNECTION_STATUS_BY_OUTCOME[event.outcome]);
      void logFirebaseConnection(event);
    });
    this.$tdm.add(() => {
      unsubscribeFromFirebaseConnectionStatus();
      firebaseConnectionMonitor.cancel();
    });

    try {
      const firebaseChat = FirebaseChatService.create({ chatId, token: firebaseToken, visitor })
        .on('onMessage', (firebaseMessage) => this.receiveNewMessage(firebaseMessage))
        .on('onTyping', (data) => this.event.emit('user:typing', data.store))
        .on('onEnd', (end) => this.closeChat(end))
        .on('onNetwork', (isConnected) => {
          firebaseConnectionMonitor.observe(isConnected);
          this.event.emit('chat:network:connected', isConnected);
        })
        .on('onNetworkError', (error) => firebaseConnectionMonitor.fail(error))
        .on('onParticipation', (data) => {
          if (
            data.type === 'member:join' &&
            data.member.userId !== SYSTEM_TRIGGER_ADMIN &&
            data.member.userId !== customerId
          ) {
            this.chatState.didAgentJoin = true;
          }
          this.event.emit('chat:member:participation', data);
          onParticipationExtra?.(data);
        });

      this.firebaseChat = firebaseChat;

      await firebaseChat.start();
    } catch (error) {
      firebaseConnectionMonitor.fail(error);
      throw error;
    }

    this.chatState.isReady = true;
    this.event.emit('chat:ready');

    analytics.track({
      name: COMPONENT_EVENTS.CONNECT_OPTIONS_CHAT_CREATED,
      selection: this.options.ticketId ?? chatId,
      metadata: { chatId },
    });
  }

  /**
   * Visitor sends are fire-and-forget in the UI, so a failed Firebase write would otherwise be
   * invisible: the message never reaches the agent. Report it and keep the rejection for callers.
   */
  protected reportSendFailure = (error: unknown): never => {
    captureEventException(error, ErrorTrackingEvent.ChatMessageSend);
    throw error;
  };

  async sendMessage(message: string, meta?: Types.ChatMessageMetadata) {
    devLog.debug('[PLEASED_CHAT] Sending message', { chatId: this.id, message });
    if (!this.chatState.isReady) await this.once('chat:ready');

    const chatMessage: Types.FirebaseChatMessage = {
      attachmentFlag: false,
      createdAt: await fbTimestamp(),
      from: 'customer',
      to: 'agent',
      message,
      userId: this.visitor?.id as number,
      ...meta,
    };
    if (!this.firebaseChat) return;
    try {
      await this.firebaseChat.sendMessage(chatMessage);
      this.event.emit('chat:customer:activity');
    } catch (error) {
      this.reportSendFailure(error);
    }
  }

  async sendFile(file: File, message?: string, meta?: Types.ChatMessageMetadata) {
    if (!this.chatState.isReady) await this.once('chat:ready');

    const attachment = await PleasedApi.uploadAttachment(file);

    const chatMessage: Types.FirebaseChatMessage = {
      createdAt: await fbTimestamp(),
      from: 'customer',
      to: 'agent',
      message: message || '',
      userId: this.visitor?.id as number,
      attachmentFlag: true,
      attachmentId: attachment.id,
      attachmentName: attachment.fileName,
      contentType: attachment.contentType,
      attachmentUrl: attachment.url,
      ...meta,
    };

    if (!this.firebaseChat) return;
    try {
      await this.firebaseChat.sendMessage(chatMessage);
      this.event.emit('chat:customer:activity');
    } catch (error) {
      this.reportSendFailure(error);
    }
  }

  /** Typing activity reflects local input intent (throttled), not Firebase write confirmation. */
  async setTypingStatus(isTyping: boolean): Promise<void> {
    if (isTyping) {
      this.emitThrottledTypingActivity();
    }

    return this.firebaseChat?.setTyping(isTyping);
  }

  protected receiveNewMessage({ message, key }: { message: Types.FirebaseChatMessage; key: string }) {
    devLog.debug('[PLEASED_CHAT] Receiving new message', { chatId: this.id, message });
    if (!this.firebaseChat) return;

    // Notify the system message broker of new messages
    this.systemMessageBroker?.notifyNewMessage(message);

    const user = this.firebaseChat?.getChatMember(message.userId);
    const chatMessage = createChatMessage({
      id: key || `no-snap-key-${randomId()}`,
      authorName: user.name || 'Default Nick',
      authorType: message.from,
      authorId: message.userId,
      message: message.message,
      timestamp: message.createdAt as number,
      userSelection: message.userSelection,
      attachment: createAttachment({
        contentType: message.contentType,
        id: message.attachmentId,
        name: message.attachmentName,
        url: message.attachmentUrl,
      }),
    });

    this.chatState.messages[chatMessage.id] = chatMessage;
    // Emit a copy of the message.
    this.event.emit('chat:message', createChatMessage(chatMessage));
  }

  protected async closeChat(closedBy?: Types.FirebaseClosedChat) {
    if (this.chatState.closed) return;
    // Cleanup
    const closedChatDetails = closedBy || {
      endedAt: Date.now(),
      from: 'customer',
      name: this.visitor?.displayName || '',
      ticketId: this.options.ticketId?.toString?.() || '',
    };
    this.chatState.closed = closedChatDetails;

    this.firebaseChat?.stopPresenceHeartbeat();
    this.firebaseChat?.cancelPresenceDisconnect();

    const setUserOffline = this.firebaseChat?.setPresence(
      {
        status: 'offline',
        reason: closedBy ? 'end-chat-remote' : 'end-chat-local',
      },
      { force: true }
    );
    const resetUserTyping = this.firebaseChat?.makeSureNoOneIsTyping();
    this.firebaseChat?.makeAllMembersLeave();

    await Promise.all([setUserOffline, resetUserTyping]);

    this.firebaseChat?.destroy();
    this.systemMessageBroker?.destroy();
    this.systemMessageBroker = null;
    this.$tdm.teardown();

    this.event.emit('chat:closed', { reason: closedBy ? 'remote' : 'local' });
    // Remove events
    this.event.all.clear();
  }

  /**
   * End the chat from user's side.
   */
  async end() {
    // if the chat is already closed or was never ever started, this
    // does nothing. If the chat was already closed & this is used
    // for clean up, then this will not report the chat ended again.
    devLog.debug('[PLEASED_CHAT] Ending chat', { chatId: this.id });
    if (this.chatState.closed || !this.chatState.isReady || !this.chatId) return;
    const { chatId } = this;

    await this.closeChat();

    await PleasedApi.reportChatEnded.once({ chatId }).catch(noop);
  }

  /**
   * Sets up Firebase listener for AI chat intervention
   * @param aiSessionId - The AI session ID to monitor for intervention
   * @param firebaseToken - Firebase token for authentication
   * @param onIntervene - Callback when intervention is triggered with the chat ID
   * @returns Cleanup function to remove the listener
   */
  static setupAiChatIntervention(aiSessionId: string, firebaseToken: string, onIntervene: (chatId: number) => void) {
    return FirebaseChatService.setupAiSessionIntervention(aiSessionId, firebaseToken, onIntervene);
  }

  /**
   * Creates a PleasedChat instance with visitor details (without calling create())
   * @param visitor - Visitor details
   * @param chatId - Chat ID
   * @returns PleasedChat instance
   */
  protected static createInstance(visitor: VisitorDetail, chatId: number): PleasedChat {
    const chat = new PleasedChat({
      user: {
        id: visitor.id,
        displayName: visitor.displayName,
        email: visitor.email,
        phone: visitor.phone,
        foreignId: visitor.foreignId,
        isGuest: visitor.isGuest,
      },
      userSelections: [],
    });

    chat.visitor = visitor;
    chat.chatId = chatId;

    return chat;
  }

  /**
   * Loads an existing chat (for AI intervention or resuming a chat)
   * @param payload - Configuration object
   * @param payload.chatId - The chat ID
   * @param payload.visitor - Visitor details
   * @param payload.firebaseToken - Firebase token for authentication
   * @param payload.timeSinceTriggers - System message triggers
   * @param payload.generateSessionId - If true, generates a new resumable session ID
   * @param payload.skipFirebaseSetup - If true, Firebase setup will be skipped and must be called manually
   * @returns PleasedChat instance
   */
  static async loadExistingChat(payload: {
    chatId: number;
    visitor: VisitorDetail;
    firebaseToken: string;
    timeSinceTriggers?: SystemMessageConfig[];
    generateSessionId?: boolean;
    skipFirebaseSetup?: boolean;
  }) {
    const { chatId, visitor, firebaseToken, timeSinceTriggers, generateSessionId, skipFirebaseSetup } = payload;

    const chat = PleasedChat.createInstance(visitor, chatId);

    const resumableChatSessionId = generateSessionId ? randomUUID() : undefined;
    if (resumableChatSessionId) {
      chat.resumableChatSessionId = resumableChatSessionId;
    }

    chat.brokerCreation(timeSinceTriggers || [], resumableChatSessionId);

    if (!skipFirebaseSetup) {
      await chat.setupFirebaseChat({
        chatId,
        firebaseToken,
        visitor,
      });
    }

    return chat;
  }

  /**
   * Sends a resume message with special kind flag
   * @param message - Message content
   */
  async sendResumeMessage(message: string) {
    if (!message || !this.chatState.isReady) return;

    const resumeMessage: Types.FirebaseChatMessage = {
      createdAt: await fbTimestamp(),
      from: 'customer',
      attachmentFlag: false,
      userId: Number(this.visitor?.id),
      message,
      to: 'agent',
      userSelection: false,
      kind: 'user-resumed-chat',
      resumableChatSessionId: this.resumableChatSessionId,
    };

    await this.firebaseChat?.sendMessage(resumeMessage);
    this.event.emit('chat:customer:activity');
  }

  /**
   * Public method to setup Firebase chat (used when skipFirebaseSetup is true in loadCreatedChat)
   * @param firebaseToken - Firebase token for authentication
   */
  async connectFirebase(firebaseToken: string) {
    if (!this.chatId || !this.visitor) {
      throw new Error('Chat must be loaded before connecting to Firebase');
    }

    await this.setupFirebaseChat({
      chatId: this.chatId,
      firebaseToken,
      visitor: this.visitor,
    });
  }

  protected brokerCreation(systemTriggers: SystemMessageConfig[], resumableChatSessionId?: string) {
    const broker = new SystemTriggerMessageBroker(systemTriggers, resumableChatSessionId);
    this.systemMessageBroker = broker;
    broker.onMatch((match) => {
      this.sendSystemMessage(match.message || '');
    });
  }

  protected async sendSystemMessage(message: string) {
    const chatMessage: Types.FirebaseChatMessage = {
      createdAt: await fbTimestamp(),
      from: 'agent',
      attachmentFlag: false,
      userId: SYSTEM_TRIGGER_ADMIN,
      message,
      to: 'customer',
    };
    await this.firebaseChat?.sendMessage(chatMessage);
  }
}
